<script lang="ts" setup>
import { computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router/composables'
import { useSessionStorage } from '@vueuse/core'
import { loginConfig as config } from './configs/vowpath'
import { useHistoryStore } from '@/stores'
import { useTripartiteLogin } from '@/composables/useTripartiteLogin'
import { useEmailLogin } from '@/composables/useEmailLogin'
import { appConfig } from '@/composables/useAppConfig'
import { directPageViewTrack, useElementClick } from '@/composables/tracks'
import { LOCALE_MAP, type LocaleCode, setLocale } from '@/i18n'

const route = useRoute()
const router = useRouter()
const historyStore = useHistoryStore()
const showLogout = localStorage.getItem('logout')
const deviceUuid = useSessionStorage<any>('did', undefined)
const redirect = useSessionStorage<any>('redirect', undefined)
const loginToken = useSessionStorage<any>('loginToken', undefined)
const { elementClickForLogin } = useElementClick()

const redirectUrl = route.query.redirect as string
const d = route.query.d as string
const t = route.query.t as string
const from = (route.query.from || 'Profile') as string
const lang = route.query.lang as string | undefined

const init = function () {
  // If host passes lang, honor it first (and persist)
  if (lang && (lang in LOCALE_MAP))
    setLocale(lang as LocaleCode)

  if (redirectUrl)
    redirect.value = redirectUrl

  const query = { ...route.query }
  if (d) {
    deviceUuid.value = d
    delete query.d
  }

  if (t) {
    loginToken.value = t
    delete query.t
  }

  // Clean up transient query params
  if (lang)
    delete query.lang

  router.replace({
    query: { ...query },
  })

  directPageViewTrack('Login', from)
}
init()

const availableLogins = computed(() => appConfig.value?.login || [])

watch(appConfig, (config) => {
  if (config && config.login.length === 0 && !route.query.emailLogin) {
    router.replace({
      query: {
        ...route.query,
        emailLogin: '1',
      },
    })
  }
}, { immediate: true })

const { skeleton, signInWithGoogle, signInWithFacebook, signInWithApple, logout } = useTripartiteLogin(deviceUuid.value, loginToken.value, redirect.value)

const isEmailLogin = computed({
  get: () => ~~route.query.emailLogin,
  set: (v: any) => (route.query.emailLogin = `${v}`),
})

function setEmailLogin() {
  elementClickForLogin('login_email', {
    route_title: from,
    route_host: new URL(redirect.value).host,
    route_url: redirect.value,
  })
  historyStore.$push(window.location.href)
  router.push({
    query: {
      ...route.query,
      emailLogin: '1',
    },
  })
}

const back = function () {
  window.history.go(-1)
}

const { form, getCode, beforeSignIn, resetEmail } = useEmailLogin(deviceUuid.value, loginToken.value, redirect.value)

watch(
  () => isEmailLogin.value,
  () => resetEmail(),
)

// 从环境变量获取链接
const userAgreementUrl = import.meta.env.VITE_USER_AGREEMENT_URL
const privacyPolicyUrl = import.meta.env.VITE_PRIVACY_POLICY_URL
const facebookBlue = config.theme.facebookBlue
const googleBlue = config.theme.googleBlue
const appleDark = config.theme.appleDark
</script>

<template>
  <div class="login-page bg-n-bg-global text-n-text-1">
    <template v-if="!skeleton">
      <header class="login-header">
        <button type="button" class="login-back" aria-label="Back" @click="back">
          <i-icon-park-outline-left class="w-6 h-6" />
        </button>
        <h1 class="text-18px font-bold">
          {{ isEmailLogin ? config.texts.signInWithEmail : config.texts.title }}
        </h1>
      </header>

      <main v-if="!isEmailLogin" class="login-main">
        <div class="login-brand">
          <img :src="config.resources.logo" class="login-app-icon" alt="Vowpath">
          <img :src="config.resources.novel" class="login-wordmark" alt="Vowpath">
        </div>
        <div class="login-actions">
          <button v-if="availableLogins.includes('facebook')" type="button" class="login-third-btn facebook-btn" @click="signInWithFacebook">
            <img :src="config.resources.facebookIcon" alt="">
            <span>{{ config.texts.signInWithFacebook }}</span>
          </button>
          <button v-if="availableLogins.includes('google')" type="button" class="login-third-btn google-btn" @click="signInWithGoogle">
            <i-logos-google-icon class="login-provider-icon" />
            <span>{{ config.texts.signInWithGoogle }}</span>
          </button>
          <button v-if="availableLogins.includes('apple')" type="button" class="login-third-btn apple-btn" @click="signInWithApple">
            <img :src="config.resources.appleIcon" alt="">
            <span>{{ config.texts.signInWithApple }}</span>
          </button>
          <button v-if="showLogout === '1'" type="button" class="login-third-btn google-btn" @click="logout">
            {{ config.texts.logout }}
          </button>
          <button type="button" class="email-link text-n-primary" @click="setEmailLogin">
            {{ config.texts.continueWithEmail }}
          </button>
        </div>
      </main>

      <main v-else class="email-main">
        <h2 class="email-heading">
          {{ config.texts.continueWithEmail }}
        </h2>
        <p class="email-description text-n-text-2">
          {{ config.texts.emailDescription }}
        </p>
        <form class="email-form" @submit.prevent="beforeSignIn">
          <label for="login-email">{{ config.texts.yourEmail }}</label>
          <input id="login-email" v-model="form.email" type="email" autocomplete="email" maxlength="50" class="login-input">
          <label for="login-code" class="code-label">{{ config.texts.verificationCode }}</label>
          <div class="code-row">
            <input id="login-code" v-model="form.code" type="text" inputmode="numeric" autocomplete="one-time-code" maxlength="6" class="login-input">
            <button type="button" class="send-code" :disabled="form.timeout > 0" @click="getCode">
              {{ form.timeout > 0 ? `${form.timeout}s` : config.texts.getVerificationCode }}
            </button>
          </div>
          <button type="submit" class="email-submit">
            {{ config.texts.signIn }}
          </button>
        </form>
      </main>

      <p class="login-agreement text-n-text-2" :class="{ 'login-agreement--email': isEmailLogin }">
        {{ config.texts.userAgreement }} <a :href="userAgreementUrl">{{ config.texts.userAgreementLink }}</a> {{ config.texts.and }} <a :href="privacyPolicyUrl">{{ config.texts.privacyPolicyLink }}</a>
      </p>
    </template>
    <div v-else class="pt-20vh text-center">
      <BasicSpinner class="mx-auto" />
      <p class="mt-4 font-bold">
        {{ config.texts.loading }}
      </p>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.login-page {
  min-height: 100dvh;
  padding-bottom: calc(32px + env(safe-area-inset-bottom));
}

.login-header {
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.login-back {
  position: absolute;
  left: 12px;
  padding: 8px;
}

.login-main {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.login-brand {
  margin-top: 95px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.login-app-icon {
  width: 76px;
  height: 75px;
  border-radius: 20px;
}

.login-wordmark {
  width: 124px;
  height: auto;
  margin-top: 24px;
}

.login-actions {
  width: calc(100% - 82px);
  margin-top: 106px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
}

.login-third-btn {
  min-height: 46px;
  border-radius: 25px;
  padding: 10px 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  line-height: 17px;

  & + & {
    margin-top: 14px;
  }

  img,
  .login-provider-icon {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    object-fit: contain;
    margin-right: 10px;
  }

  span {
    min-width: 0;
  }
}

.facebook-btn {
  background: v-bind(facebookBlue);
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.google-btn {
  background: v-bind(googleBlue);
  color: #333;
}

.apple-btn {
  background: v-bind(appleDark);
  border: 1px solid rgba(255, 255, 255, 0.12);
  font-weight: 400;
}

.email-link {
  margin-top: 20px;
  font-size: 12px;
  line-height: 18px;
}

.login-agreement {
  max-width: 270px;
  margin: 24px auto 0;
  text-align: center;
  font-size: 10px;
  line-height: 16px;

  a {
    color: #996aaa;
  }
}

.email-main {
  padding: 0 17px;
}

.email-heading {
  margin-top: 55px;
  text-align: center;
  font-size: 22px;
  font-weight: 700;
  line-height: 30px;
  background: linear-gradient(90deg, #a658ff, #ff5acd);
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
}

.email-description {
  max-width: 270px;
  margin: 10px auto 0;
  text-align: center;
  font-size: 12px;
  line-height: 15px;
}

.email-form {
  margin-top: 60px;

  label {
    display: block;
    font-size: 12px;
    line-height: 18px;
    color: #c8c7dc;
    margin-bottom: 8px;
  }

  .code-label {
    margin-top: 19px;
  }
}

.login-input {
  width: 100%;
  min-width: 0;
  height: 40px;
  padding: 0 12px;
  border: 1px solid #9797a8;
  border-radius: 8px;
  background: transparent;
  color: inherit;
  font-size: 16px;
  outline: none;

  &:focus {
    border-color: #bb7afb;
  }
}

.code-row {
  display: flex;
  align-items: stretch;

  .login-input {
    flex: 1;
  }
}

.send-code {
  margin-left: 17px;
  width: 87px;
  flex-shrink: 0;
  padding: 8px;
  border-radius: 8px;
  background: #342d58;
  font-size: 10px;

  &:disabled {
    opacity: 0.5;
  }
}

.email-submit {
  display: block;
  width: calc(100% - 30px);
  min-height: 40px;
  margin: 52px auto 0;
  padding: 10px 16px;
  border-radius: 24px;
  background: linear-gradient(90deg, #9a57ff, #ff5acd);
  font-size: 14px;
  font-weight: 700;
}

.login-agreement--email {
  margin-top: 22px;
}

@media (max-height: 650px) {
  .login-brand { margin-top: 45px; }
  .login-actions { margin-top: 55px; }
  .email-heading { margin-top: 30px; }
  .email-form { margin-top: 35px; }
}
</style>
